import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, CircularProgress, Alert, Snackbar,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  CheckRounded, AddRounded, RemoveRounded, LockRounded, VerifiedUserRounded,
  MedicalServicesRounded, ConfirmationNumberRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useSubscription, RazorpayPlan } from '../../hooks/useSubscription';
import { useRazorpay } from '../../hooks/useRazorpay';
import { getUser, getBusinessName } from '../../auth/session';
import { rs, isYearlyPeriod, typeBadge } from './billingUtils';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const PlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { fetchPlans, createSubscription, verifyPayment } = useSubscription();
  const { openCheckout } = useRazorpay();

  const [plans, setPlans] = useState<RazorpayPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedId, setSelectedId] = useState<string>('');
  const [qty, setQty] = useState(1);
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setPlans(await fetchPlans()); }
      catch (e: any) { setError(e?.response?.data?.message || 'Failed to load plans.'); }
      finally { setLoading(false); }
    })();
  }, [fetchPlans]);

  const visible = useMemo(
    () => plans.filter((p) => (billing === 'yearly' ? isYearlyPeriod(p.period) : !isYearlyPeriod(p.period))),
    [plans, billing]
  );

  // keep a valid selection for the current billing period
  useEffect(() => {
    if (visible.length && !visible.some((p) => p.id === selectedId)) { setSelectedId(visible[0].id); setQty(1); }
  }, [visible, selectedId]);

  const selected = visible.find((p) => p.id === selectedId) || null;
  const total = selected ? selected.amount * qty : 0;

  const subscribe = async () => {
    if (!selected) return;
    setPaying(true); setError(null);
    try {
      // Backend resolves the plan by razorpay_plan_id (not the internal UUID id).
      const res = await createSubscription({ plan_id: selected.razorpay_plan_id, quantity: qty });
      if (!res.payment) { setToast('Subscription activated'); setTimeout(() => navigate('/subscriptions'), 600); return; }
      const u = getUser();
      const rp = await openCheckout({
        subscription_id: res.payment.subscription_id,
        key_id: res.payment.key_id,
        name: getBusinessName(),
        description: `${selected.name} × ${qty}`,
        prefill: { name: u?.display_name || undefined, contact: u?.phone_number || undefined },
      });
      await verifyPayment({
        subscription_id: res.subscription.id,
        razorpay_subscription_id: rp.razorpay_subscription_id,
        razorpay_payment_id: rp.razorpay_payment_id,
        razorpay_signature: rp.razorpay_signature,
      });
      setToast('Payment successful — subscription active!');
      setTimeout(() => navigate('/subscriptions'), 800);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Payment failed.');
    } finally { setPaying(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
        <Box>
          <Typography variant="h3">Choose your plan</Typography>
          <Typography color="text.secondary">Pay only for the providers and tokens you need. Cancel anytime.</Typography>
        </Box>
        <ToggleButtonGroup exclusive size="small" value={billing} onChange={(_, v) => v && setBilling(v)} sx={{ height: 40 }}>
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Alert severity="info">No {billing} plans available.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: 2.25, alignItems: 'start' }}>
          {/* Plan list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {visible.map((p) => {
              const sel = p.id === selectedId;
              const tb = typeBadge(p.type);
              return (
                <Box key={p.id} onClick={() => { setSelectedId(p.id); setQty(1); }}
                  sx={{ ...card, p: 2.25, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, border: `1.5px solid ${sel ? colors.primary : colors.border}`, bgcolor: sel ? colors.primaryLight : colors.backgroundPaper }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip size="small" label={tb.label} sx={{ bgcolor: tb.bg, color: tb.fg, fontWeight: 600, height: 22 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{p.name}</Typography>
                      {sel && <Chip size="small" icon={<CheckRounded />} label="Selected" sx={{ bgcolor: colors.primaryLight, color: colors.primary, fontWeight: 600, height: 22 }} />}
                    </Box>
                    {p.description && <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.5 }}>{p.description}</Typography>}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, color: colors.textSecondary, fontSize: 13 }}>
                      {p.provider_count > 0 && <span><MedicalServicesRounded sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />{p.provider_count} provider{p.provider_count > 1 ? 's' : ''}</span>}
                      {p.token_count > 0 && <span><ConfirmationNumberRounded sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />{p.token_count.toLocaleString('en-IN')} tokens</span>}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{rs(p.amount)}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>/{billing === 'yearly' ? 'year' : 'month'} per unit</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Summary */}
          {selected && (
            <Box sx={{ ...card, p: 2.5, position: { md: 'sticky' }, top: 90 }}>
              <Typography variant="h5" sx={{ mb: 1.5 }}>Summary</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip size="small" label={typeBadge(selected.type).label} sx={{ bgcolor: typeBadge(selected.type).bg, color: typeBadge(selected.type).fg, fontWeight: 600, height: 22 }} />
                <Typography sx={{ fontWeight: 700 }}>{selected.name}</Typography>
              </Box>

              <Typography sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Quantity</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <IconButton onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} sx={{ border: `1px solid ${colors.border}` }}><RemoveRounded /></IconButton>
                <Typography sx={{ fontSize: 24, fontWeight: 800, minWidth: 40, textAlign: 'center' }}>{qty}</Typography>
                <IconButton onClick={() => setQty((q) => q + 1)} sx={{ border: `1px solid ${colors.border}` }}><AddRounded /></IconButton>
              </Box>

              <Box sx={{ borderTop: `1px solid ${colors.border}`, mt: 2, pt: 1.5 }}>
                <Row label="Providers" value={selected.provider_count * qty} />
                <Row label="Tokens" value={(selected.token_count * qty).toLocaleString('en-IN')} />
                <Row label="Subtotal" value={rs(total)} />
              </Box>
              <Box sx={{ borderTop: `1px solid ${colors.border}`, mt: 1.5, pt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Typography color="text.secondary">Total</Typography>
                <Typography><span style={{ fontSize: 24, fontWeight: 800 }}>{rs(total)}</span><span style={{ color: colors.textSecondary }}>/{billing === 'yearly' ? 'year' : 'month'}</span></Typography>
              </Box>

              <Button fullWidth variant="contained" startIcon={paying ? undefined : <LockRounded />} disabled={paying} onClick={subscribe} sx={{ mt: 2 }}>
                {paying ? <CircularProgress size={22} color="inherit" /> : 'Subscribe securely'}
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 2, color: colors.textTertiary, fontSize: 13 }}>
                <VerifiedUserRounded sx={{ fontSize: 16 }} /> Payments secured by Razorpay
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: 14 }}>
    <Typography sx={{ color: colors.textSecondary, fontSize: 14 }}>{label}</Typography>
    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{value}</Typography>
  </Box>
);

export default PlansPage;
